import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDashboardTasksListQuery } from 'api';
import { TaskItem } from './TaskItem';
import { FilterButton } from '../FilterButton';
import { Col, EditableInfoComponent, Row, Skeleton } from 'components';
import { AllEntityTypes, StatusTypes, Task } from 'models/Task';
import { TaskIconGreen } from 'components/common/icons/TaskIconGreen';
import styles from '../dashboard.module.scss';
import { StatusType } from 'components/common/StatusBadge';

const allStatusType = { value: 'ALL', label: 'All' };

export const DashboardTasks = memo(() => {
  const userId = JSON.parse(localStorage.getItem('userId') || 'null');
  const { data: { content: allTasks = [] } = {}, isLoading } = useDashboardTasksListQuery(
    { userId },
    { skip: !userId }
  );
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');

  const tasks = useMemo(() => {
    return allTasks.filter((i: { status: StatusType }) => i.status !== 'DONE');
  }, [allTasks]);

  const onEntityClick = useCallback((type: string, id: string) => {
    let path = `${AllEntityTypes[type]?.path}/${id}`;
    if (AllEntityTypes[type]?.tab) {
      path = `${path}/${AllEntityTypes[type]?.tab}`;
    }
    return path;
  }, []);

  const handleStatusFilter = useCallback((status: string) => {
    setSelectedStatus(status);
  }, []);

  // Status counts calculation
  const statusCounts = useMemo(() => {
    return tasks.reduce(
      (acc: Record<string, number>, task: Task) => {
        const status = task.status || 'TODO';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      { ALL: tasks.length }
    );
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    return selectedStatus === 'ALL' ? tasks : tasks.filter((task: Task) => task.status === selectedStatus);
  }, [tasks, selectedStatus]);

  return (
    <Row gutter={[12, 12]} className={styles.tasks_section}>
      <Col span={24}>
        <EditableInfoComponent title={'My Tasks'} editable={false} icon={<TaskIconGreen />} />
      </Col>
      <Col span={24}>
        <Row className="task-filters" wrap={false}>
          <FilterButton
            key="ALL"
            label={allStatusType.label}
            count={statusCounts.ALL || 0}
            isSelected={selectedStatus === 'ALL'}
            onClick={() => handleStatusFilter('ALL')}
          />
          {StatusTypes.filter((i) => i.value !== 'DONE').map((status) => (
            <FilterButton
              key={status.value}
              label={status.label}
              count={statusCounts[status.value] || 0}
              isSelected={selectedStatus === status.value}
              onClick={() => handleStatusFilter(status.value)}
            />
          ))}
        </Row>
      </Col>
      {isLoading ? (
        <Skeleton active />
      ) : (
        filteredTasks.map((task: Task) => <TaskItem key={task.id} task={task} onEntityClick={onEntityClick} />)
      )}
      <Col></Col>
    </Row>
  );
});
